import React, { ChangeEventHandler, FC, FocusEventHandler, Fragment } from 'react';
import { useQuery } from '@apollo/client';
import { Autocomplete, TextField } from '@mui/material';
import { getLabsTemplate } from 'apollo/templates/user/query.graphql';
import { OrgDropdown } from 'models/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';

type AdminLabsAutocompleteProps = {
    onFilterChange: (lab: OrgDropdown | null) => void;
    value?: OrgDropdown | null;
    name?: string;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    helperText?: string | boolean;
    error?: boolean;
    fullWidth?: boolean;
};

const AdminLabsAutocomplete: FC<AdminLabsAutocompleteProps> = ({
    onFilterChange,
    value,
    name,
    onChange,
    onBlur,
    helperText,
    error,
    fullWidth,
}: AdminLabsAutocompleteProps) => {
    const { t: translate } = useTranslation(['common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { data: { labs } = {}, loading: isLabsLoading } = useQuery(getLabsTemplate, {
        notifyOnNetworkStatusChange: true,
        onError(error) {
            showBoundary(error);
        },
    });

    const changeHandler = (_: React.SyntheticEvent, lab: OrgDropdown | null) => {
        onFilterChange(lab);
    };

    return (
        <Fragment>
            {!isLabsLoading && (
                <Autocomplete
                    fullWidth={fullWidth}
                    options={labs}
                    getOptionLabel={(option: OrgDropdown) => option.name || ''}
                    isOptionEqualToValue={(option: OrgDropdown, value: OrgDropdown) => option?._id === value?._id}
                    renderOption={(props, option: OrgDropdown) => (
                        <li {...{ ...props, key: option?._id }}>{option?.name || ''}</li>
                    )}
                    id="select-organization"
                    onChange={changeHandler}
                    value={value}
                    renderInput={(params) => (
                        <TextField
                            required
                            sx={{ minWidth: fullWidth ? 0 : 300 }}
                            {...params}
                            label={translate('common:LAB')}
                            id="organization"
                            value={value}
                            name={name}
                            onChange={onChange}
                            onBlur={onBlur}
                            helperText={helperText}
                            error={error}
                        />
                    )}
                />
            )}
        </Fragment>
    );
};

export default AdminLabsAutocomplete;
